import React, { useCallback, useMemo } from 'react';
import Button from '../ui/Button';
import type { SearchSelectOption } from '../ui/SearchSelect';
import type { ContactFilterType } from '../../types/contacts/contacts.types';
import type {
  Condition,
  ConditionValue,
} from '../../types/segments/segments.types';
import { CONDITION_OPTIONS } from '../../constants/segmentConstants';
import ConditionsField, {
  type FieldOption,
} from '../shared/ConditionFields/ConditionFields';

/** Data source options for contacts: Attribute, Segment, Event */
const CONTACTS_DATA_SOURCE_OPTIONS = {
  ...CONDITION_OPTIONS,
  Event: [{ value: 'event', label: 'Event' }],
};

export interface SegmentOption {
  _id?: string | null;
  name?: string | null;
}

export interface EventOption {
  event: string;
}

export interface ContactsConditionFilterProps {
  filterType: ContactFilterType;
  onFilterTypeChange: (value: ContactFilterType) => void;
  conditionField: string;
  onConditionFieldChange: (value: string) => void;
  conditionOperator: string;
  onConditionOperatorChange: (value: string) => void;
  conditionValue:
    | string
    | ConditionValue
    | (Condition & { arrayType?: string });
  onConditionValueChange: (
    value: string | ConditionValue | (Condition & { arrayType?: string })
  ) => void;
  conditionEvent: string;
  onConditionEventChange: (value: string) => void;
  conditionSegmentId: string;
  onConditionSegmentIdChange: (value: string) => void;
  canApply: boolean;
  onApply: () => void;
  onClear: () => void;
  attributeFieldOptions: SearchSelectOption[];
  segments: SegmentOption[];
  events: EventOption[];
}

const ContactsConditionFilter: React.FC<ContactsConditionFilterProps> = ({
  filterType,
  onFilterTypeChange,
  conditionField,
  onConditionFieldChange,
  conditionOperator,
  onConditionOperatorChange,
  conditionValue,
  onConditionValueChange,
  conditionEvent,
  onConditionEventChange,
  conditionSegmentId,
  onConditionSegmentIdChange,
  canApply,
  onApply,
  onClear,
  attributeFieldOptions,
  segments,
  events,
}) => {
  const condition: Condition = useMemo(() => {
    const field =
      filterType === 'attribute'
        ? conditionField
        : filterType === 'segment'
          ? conditionSegmentId
          : conditionEvent;
    const val = conditionValue;
    const isObj = typeof val === 'object' && val !== null;
    const withArrayType = isObj && 'arrayType' in val;
    const withProperty = isObj && 'property' in val;
    const valueForCondition = withArrayType
      ? (val as { value?: unknown }).value
      : withProperty
        ? val
        : val;
    const base: Condition = {
      condition: filterType,
      field,
      operator: conditionOperator,
      value: valueForCondition as Condition['value'],
    };
    if (withArrayType) {
      base.arrayType = (val as Condition & { arrayType?: string }).arrayType;
    }
    return base;
  }, [
    filterType,
    conditionField,
    conditionSegmentId,
    conditionEvent,
    conditionOperator,
    conditionValue,
  ]);

  const setCondition = useCallback(
    (c: Condition) => {
      onFilterTypeChange((c.condition ?? 'attribute') as ContactFilterType);
      const field = c.field ?? '';
      if (c.condition === 'attribute') onConditionFieldChange(field);
      else if (c.condition === 'segment') onConditionSegmentIdChange(field);
      else if (c.condition === 'event') onConditionEventChange(field);
      onConditionOperatorChange(c.operator ?? '');
      if (c.arrayType !== undefined) {
        onConditionValueChange({
          arrayType: c.arrayType,
          value: c.value,
        } as Condition & {
          arrayType?: string;
        });
      } else if (c.property !== undefined) {
        onConditionValueChange(c);
      } else {
        onConditionValueChange(
          (c.value ?? '') as
            | string
            | ConditionValue
            | (Condition & { arrayType?: string })
        );
      }
    },
    [
      onFilterTypeChange,
      onConditionFieldChange,
      onConditionSegmentIdChange,
      onConditionEventChange,
      onConditionOperatorChange,
      onConditionValueChange,
    ]
  );

  const attributeOptions: FieldOption[] = useMemo(
    () =>
      attributeFieldOptions.map((o) => ({
        label: o.label,
        value: o.value,
        tag: (o as SearchSelectOption & { tag?: string }).tag,
      })),
    [attributeFieldOptions]
  );

  const segmentOptions: FieldOption[] = useMemo(
    () =>
      segments.map((s) => ({
        label: s.name ?? s._id ?? '',
        value: s._id ?? '',
        tag: '',
      })),
    [segments]
  );

  const eventOptions: FieldOption[] = useMemo(
    () => events.map((e) => ({ label: e.event, value: e.event, tag: '' })),
    [events]
  );

  return (
    <div className="flex flex-wrap items-end gap-4">
      <div className="flex flex-wrap items-end gap-4 w-full">
        <ConditionsField
          condition={condition}
          setCondition={setCondition}
          dataSourceOptions={CONTACTS_DATA_SOURCE_OPTIONS}
          attributeFieldOptions={attributeOptions}
          segmentOptions={segmentOptions}
          eventOptions={eventOptions}
        />
      </div>
      <div className="flex items-center gap-2">
        <Button
          variant="primary"
          size="medium"
          disabled={!canApply}
          onClick={onApply}
        >
          Apply
        </Button>
        <Button
          variant="outline"
          size="medium"
          disabled={!canApply}
          onClick={onClear}
        >
          Clear
        </Button>
      </div>
    </div>
  );
};

export default ContactsConditionFilter;
